import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useTheme as useThemeContext } from '../contexts/ThemeContext';
import usersService, { CreateRoleData, Role, UpdateRoleData } from '../services/usersService';
import { getZoomCompensatedAutocompleteSlotProps } from '../utils/zoomCompensation';
import { ROLE_NAME_MAX_LENGTH, ROLE_NAME_RULE, normalizeRoleName, validateRoleName } from '../utils/roles';
import { useFormFieldErrors } from '../hooks/useFormFieldErrors';
import FormValidationAlert from './FormValidationAlert';

interface RolesSectionProps {
  createRequested?: boolean;
  onCreateRequestHandled?: () => void;
  onRolesChanged?: () => void;
}

const RolesSection: React.FC<RolesSectionProps> = ({
  createRequested = false,
  onCreateRequestHandled,
  onRolesChanged,
}) => {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode } = useThemeContext();

  const consoleColors = {
    deepOcean: '#0f2134',
    abyssal: '#0a1622',
    sand: '#ccba9b',
    sandDim: 'rgba(204, 186, 155, 0.5)',
    text: '#ffffff',
    textDim: 'rgba(255, 255, 255, 0.7)',
  };

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [saving, setSaving] = useState(false);
  const fieldErrors = useFormFieldErrors();

  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    group: 'job' | 'category';
    clone_permissions_from_role_id: number | null;
  }>({
    name: '',
    description: '',
    group: 'job',
    clone_permissions_from_role_id: null,
  });

  const isEditMode = Boolean(selectedRole);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const rolesData = await usersService.getRoles();
      setRoles(rolesData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    if (createRequested) {
      openCreateDialog();
      onCreateRequestHandled?.();
    }
  }, [createRequested, onCreateRequestHandled]);

  const cloneOptions = useMemo(
    () => roles.filter((role) => !selectedRole || role.id !== selectedRole.id),
    [roles, selectedRole]
  );

  const openCreateDialog = () => {
    setSelectedRole(null);
    setFormData({
      name: '',
      description: '',
      group: 'job',
      clone_permissions_from_role_id: null,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (role: Role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      group: role.group || 'job',
      clone_permissions_from_role_id: null,
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (role: Role) => {
    setSelectedRole(role);
    setDeleteDialogOpen(true);
  };

  const closeDialogs = () => {
    setDialogOpen(false);
    setDeleteDialogOpen(false);
    setSelectedRole(null);
    setFormData({
      name: '',
      description: '',
      group: 'job',
      clone_permissions_from_role_id: null,
    });
    fieldErrors.clear();
  };

  const handleSave = async () => {
    const name = normalizeRoleName(formData.name);
    const nameError = validateRoleName(name);
    if (nameError) {
      fieldErrors.show(nameError, ['name']);
      return;
    }

    try {
      setSaving(true);
      fieldErrors.clear();

      if (isEditMode && selectedRole) {
        const payload: UpdateRoleData = {
          name,
          description: formData.description.trim() || null,
          group: formData.group,
        };
        const result = await usersService.updateRole(selectedRole.id, payload);
        setSuccess(result.message);
      } else {
        const payload: CreateRoleData = {
          name,
          description: formData.description.trim() || undefined,
          group: formData.group,
          clone_permissions_from_role_id: formData.clone_permissions_from_role_id,
        };
        const result = await usersService.createRole(payload);
        setSuccess(result.message);
      }

      closeDialogs();
      await loadRoles();
      onRolesChanged?.();
    } catch (err: any) {
      fieldErrors.show(err.response?.data?.error || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole) return;

    try {
      setSaving(true);
      setError(null);
      const result = await usersService.deleteRole(selectedRole.id);
      setSuccess(result.message);
      closeDialogs();
      await loadRoles();
      onRolesChanged?.();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete role');
    } finally {
      setSaving(false);
    }
  };

  const outlinedButtonSx = {
    height: 48,
    px: 3,
    border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
    color: mode === 'dark' ? consoleColors.sand : theme.palette.primary.main,
    borderRadius: 0,
    fontWeight: 600,
    letterSpacing: '0.1em',
    '&:hover': {
      backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.1) : alpha(theme.palette.primary.main, 0.05),
      border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
    },
  };

  return (
    <Box>
      {isMobile && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreateDialog} sx={outlinedButtonSx}>
            DEFINE ROLE
          </Button>
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 0, backgroundColor: alpha('#d32f2f', 0.1), color: '#d32f2f', border: '1px solid #d32f2f' }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3, borderRadius: 0, backgroundColor: alpha('#388e3c', 0.1), color: '#388e3c', border: '1px solid #388e3c' }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Alert
        severity="info"
        sx={{
          mb: 3,
          borderRadius: 0,
          backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.08) : alpha(theme.palette.info.main, 0.08),
          border: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.3) : theme.palette.info.main}`,
        }}
      >
        New roles start with no permissions. Configure them on the Feature Access page after creation.
        Category roles without permissions will deny all features (job AND category logic).
      </Alert>

      <Paper
        sx={{
          backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff',
          border: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.3) : '#e0e0e0'}`,
          borderRadius: 0,
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: mode === 'dark' ? alpha(consoleColors.deepOcean, 0.5) : '#f5f5f5' }}>
                {['NAME', 'DESCRIPTION', 'GROUP', 'USERS', 'COMMANDS'].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      color: mode === 'dark' ? alpha(consoleColors.text, 0.7) : 'text.secondary',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      letterSpacing: '0.1em',
                      borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.2) : '#e0e0e0'}`,
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, fontFamily: 'monospace' }}>
                    LOADING ROLES…
                  </TableCell>
                </TableRow>
              ) : roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, fontFamily: 'monospace' }}>
                    NO ROLES FOUND
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow
                    key={role.id}
                    sx={{
                      '&:hover': {
                        backgroundColor:
                          mode === 'dark'
                            ? alpha(consoleColors.sand, 0.05)
                            : alpha(theme.palette.primary.main, 0.02),
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}`,
                        fontFamily: 'monospace',
                        fontWeight: 600,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {role.name}
                        {role.is_system && (
                          <Tooltip title="System role — rename/delete protected">
                            <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}`,
                        color: 'text.secondary',
                      }}
                    >
                      {role.description || '—'}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}`,
                      }}
                    >
                      <Chip
                        size="small"
                        label={(role.group || 'job').toUpperCase()}
                        color={role.group === 'category' ? 'warning' : 'info'}
                        variant="outlined"
                        sx={{ borderRadius: 0, fontFamily: 'monospace', fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}`,
                        fontFamily: 'monospace',
                      }}
                    >
                      {role.user_count ?? 0}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.1) : '#eee'}`,
                      }}
                    >
                      <Tooltip title="Edit role">
                        <IconButton size="small" onClick={() => openEditDialog(role)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          role.is_system
                            ? 'System roles cannot be deleted'
                            : (role.user_count ?? 0) > 0
                              ? 'Unassign users before deleting'
                              : 'Delete role'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={role.is_system || (role.user_count ?? 0) > 0}
                            onClick={() => openDeleteDialog(role)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialogs} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'monospace', letterSpacing: '0.08em' }}>
          {isEditMode ? 'EDIT ROLE' : 'DEFINE ROLE'}
        </DialogTitle>
        <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="NAME"
                fullWidth
                required
                value={formData.name}
                error={fieldErrors.invalid('name')}
                onChange={(e) => {
                  fieldErrors.clearField('name');
                  setFormData({ ...formData, name: e.target.value.toUpperCase() });
                }}
                disabled={Boolean(selectedRole?.is_system)}
                helperText={selectedRole?.is_system ? 'System roles cannot be renamed' : ROLE_NAME_RULE}
                inputProps={{ maxLength: ROLE_NAME_MAX_LENGTH }}
                variant="filled"
                InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="DESCRIPTION"
                fullWidth
                multiline
                minRows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                variant="filled"
                InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" disabled={isEditMode && (selectedRole?.user_count ?? 0) > 0}>
                <FormLabel
                  component="legend"
                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem', letterSpacing: '0.08em' }}
                >
                  GROUP
                </FormLabel>
                <RadioGroup
                  row
                  value={formData.group}
                  onChange={(e) =>
                    setFormData({ ...formData, group: e.target.value as 'job' | 'category' })
                  }
                >
                  <FormControlLabel value="job" control={<Radio />} label="Job" />
                  <FormControlLabel value="category" control={<Radio />} label="Category" />
                </RadioGroup>
                {isEditMode && (selectedRole?.user_count ?? 0) > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Group cannot change while users are assigned.
                  </Typography>
                )}
                {!isEditMode && formData.group === 'category' && (
                  <Alert severity="warning" sx={{ mt: 1, borderRadius: 0 }}>
                    Category roles without permissions deny all features for their holders
                    (job AND category must both allow).
                  </Alert>
                )}
              </FormControl>
            </Grid>
            {!isEditMode && (
              <Grid item xs={12}>
                <Autocomplete
                  options={cloneOptions}
                  getOptionLabel={(option) => `${option.name} (${option.group || 'job'})`}
                  value={cloneOptions.find((r) => r.id === formData.clone_permissions_from_role_id) || null}
                  onChange={(_, newValue) =>
                    setFormData({
                      ...formData,
                      clone_permissions_from_role_id: newValue?.id ?? null,
                    })
                  }
                  slotProps={getZoomCompensatedAutocompleteSlotProps()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="CLONE PERMISSIONS FROM (OPTIONAL)"
                      variant="filled"
                      helperText="Leave empty to start with no permissions"
                      InputLabelProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button onClick={closeDialogs} sx={{ color: 'text.secondary' }}>
            CANCEL
          </Button>
          <Button
            variant="outlined"
            disabled={saving}
            onClick={handleSave}
            sx={outlinedButtonSx}
          >
            {isEditMode ? 'SAVE ROLE' : 'CREATE ROLE'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteDialogOpen} onClose={closeDialogs} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: 'monospace', letterSpacing: '0.08em' }}>
          DELETE ROLE
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Delete role <strong>{selectedRole?.name}</strong>? This cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            Deleting a role also removes its CFOS folder ACL entries and feature permissions.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button onClick={closeDialogs} sx={{ color: 'text.secondary' }}>
            CANCEL
          </Button>
          <Button
            color="error"
            variant="outlined"
            disabled={saving}
            onClick={handleDelete}
            sx={{ borderRadius: 0, fontFamily: 'monospace' }}
          >
            DELETE
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesSection;
